import { Tooltip } from '@ads/components-react';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Spinner } from '@/shared/ui/atoms';

export interface SaveStatusProps {
  /** `true` enquanto o slide está sendo persistido no backend. */
  readonly isSaving: boolean;
  /** `true` quando há alterações não salvas desde o último save. */
  readonly isDirty: boolean;
}

/**
 * Indicador de estado de salvamento exibido na top bar do editor.
 *
 * Comunica, em linguagem direta e com `aria-live`, em qual dos três estados o
 * slide está: salvando, com alterações não salvas ou salvo. Em barras estreitas
 * (via container query) colapsa para apenas o ícone, com o rótulo preservado no
 * tooltip — liberando espaço horizontal para o título.
 *
 * @param props - Estados de salvamento do slide ativo
 */
export function SaveStatus({ isSaving, isDirty }: SaveStatusProps) {
  const { t } = useTranslation('slideEditor');

  let label: string;
  let icon: ReactNode;
  let tone: string;

  if (isSaving) {
    label = t('topBar.saveStatus.saving');
    icon = <Spinner size="sm" />;
    tone = 'text-ctx-content-placeholder';
  } else if (isDirty) {
    label = t('topBar.saveStatus.unsaved');
    icon = <span className="h-nano w-nano shrink-0 rounded-pill bg-base-warning-500" />;
    tone = 'text-base-warning-800';
  } else {
    label = t('topBar.saveStatus.saved');
    icon = <CheckCircleIcon className="h-micro w-micro shrink-0" />;
    tone = 'text-base-success-600';
  }

  return (
    <Tooltip title={label} side="bottom">
      <div
        role="status"
        aria-live="polite"
        className={`flex shrink-0 items-center gap-quark ts-caption-xxs ${tone}`}
      >
        {icon}
        <span className="hidden @[56rem]:inline">{label}</span>
      </div>
    </Tooltip>
  );
}
